<template>
  <div>
    <header-component />
    <div class="container-fluid mt--6">
      <div class="card">
        <div class="card-header">
          <h3 class="mb-0" style="text-transform: capitalize">
            Sale Quotations
          </h3>
        </div>

        <!-- Filter Panel -->
        <div class="flex-shrink-0 px-4 py-2 mb-2 bg-gray-50 rounded-lg border border-gray-200">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <!-- Date Range -->
            <div class="col-span-2 space-y-2">
              <label class="block text-xs font-medium text-gray-600">Date Range</label>
              <div class="flex space-x-2">
                <input
                  v-model="panelFilters.fromDate"
                  type="date"
                  class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  v-model="panelFilters.toDate"
                  type="date"
                  class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div class="flex flex-wrap gap-1">
                <button type="button" @click="setDateFilter('currentFY')" :class="dateQuickFilterClass('currentFY')">Current FY</button>
                <button type="button" @click="setDateFilter('prevFY')" :class="dateQuickFilterClass('prevFY')">Prev FY</button>
                <button type="button" @click="setDateFilter('today')" :class="dateQuickFilterClass('today')">Today</button>
                <button type="button" @click="setDateFilter('yesterday')" :class="dateQuickFilterClass('yesterday')">Yesterday</button>
                <button type="button" @click="setDateFilter('thisWeek')" :class="dateQuickFilterClass('thisWeek')">This Week</button>
                <button type="button" @click="setDateFilter('thisMonth')" :class="dateQuickFilterClass('thisMonth')">This Month</button>
                <button type="button" @click="setDateFilter('lastMonth')" :class="dateQuickFilterClass('lastMonth')">Last Month</button>
              </div>
            </div>
            <!-- Quotation Number -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Quotation Number</label>
              <input
                v-model="panelFilters.quotationNumber"
                type="text"
                @keyup.enter="applySingleFilter('quotationNumber')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <!-- Customer Name -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Customer Name</label>
              <input
                v-model="panelFilters.customerName"
                type="text"
                @keyup.enter="applySingleFilter('customerName')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <!-- Status -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Status</label>
              <select
                v-model="panelFilters.status"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="dispatched">Dispatched</option>
                <option value="accepted">Accepted</option>
                <option value="declined">Declined</option>
              </select>
            </div>
            <!-- Actions -->
            <div class="space-y-2">
              <label class="block text-xs font-medium text-gray-600">Actions</label>
              <div class="flex space-x-2">
                <button
                  @click="applyFilters"
                  class="flex-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
                >Apply</button>
                <button
                  @click="clearFilters"
                  class="flex-1 px-3 py-2 text-sm text-white bg-gray-500 rounded-md transition-colors hover:bg-gray-600"
                >Clear</button>
              </div>
            </div>
          </div>

          <!-- Active Filters -->
          <div v-if="hasActiveFilters" class="pt-4 mt-4 border-t border-gray-200">
            <div class="flex flex-wrap gap-2 items-center">
              <span class="text-xs font-medium text-gray-600">Active Filters:</span>
              <span
                v-if="appliedFilters.fromDate || appliedFilters.toDate"
                class="inline-flex items-center px-2 py-1 text-xs text-blue-800 bg-blue-100 rounded-full"
              >
                Date: {{ formatAppliedDateRange() }}
                <button @click="clearDateFilter" class="ml-1 text-blue-600 hover:text-blue-800">×</button>
              </span>
              <span
                v-if="appliedFilters.quotationNumber"
                class="inline-flex items-center px-2 py-1 text-xs text-orange-800 bg-orange-100 rounded-full"
              >
                Quotation: {{ appliedFilters.quotationNumber }}
                <button @click="clearAppliedFilter('quotationNumber')" class="ml-1 text-orange-600 hover:text-orange-800">×</button>
              </span>
              <span
                v-if="appliedFilters.customerName"
                class="inline-flex items-center px-2 py-1 text-xs text-green-800 bg-green-100 rounded-full"
              >
                Customer: {{ appliedFilters.customerName }}
                <button @click="clearAppliedFilter('customerName')" class="ml-1 text-green-600 hover:text-green-800">×</button>
              </span>
              <span
                v-if="appliedFilters.status"
                class="inline-flex items-center px-2 py-1 text-xs text-red-800 bg-red-100 rounded-full"
              >
                Status: {{ appliedFilters.status }}
                <button @click="clearAppliedFilter('status')" class="ml-1 text-red-600 hover:text-red-800">×</button>
              </span>
            </div>
          </div>
        </div>

        <!-- Custom Tailwind CSS Table -->
        <div class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                >
                  Sale Quotation No.
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                >
                  Customer
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                >
                  Type
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                >
                  Date
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                >
                  Total
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                >
                  Status
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="quotation in userQuotations"
                :key="
                  quotation.userQuotationId || quotation.serviceRequestQuotationId
                "
                class="hover:bg-gray-50"
              >
                <td
                  class="px-4 py-3 text-sm text-gray-900 border-b"
                >
                  <div class="flex flex-col gap-1">
                    <span>{{ quotation.quotationNumber || "-" }}</span>
                    <span
                      v-if="quotation.serviceRequestNumber"
                      class="text-xs text-gray-500"
                    >
                      SR No: {{ quotation.serviceRequestNumber }}
                    </span>
                  </div>
                </td>
                <td
                  class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-b"
                >
                  {{ quotation.user.firstName + " " + quotation.user.lastName }}
                </td>
                <td
                  class="px-4 py-3 text-sm text-gray-900 border-b"
                >
                  <span
                    v-if="quotation.quotationType"
                    class="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold text-center rounded-full"
                    :class="
                      isDetailedProductQuotation(quotation)
                        ? 'bg-blue-100 text-blue-800 min-w-[230px]'
                        : 'bg-gray-100 text-gray-700 min-w-[160px]'
                    "
                  >
                    {{ quotation.quotationType }}
                  </span>
                </td>
                <td
                  class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-b"
                >
                  {{ formatDate(quotation.createdAt) }}
                </td>
                <td
                  class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-b"
                >
                  ₹{{ quotation.total }}
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm border-b">
                  <span
                    v-if="quotation.status"
                    class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                    :class="{
                      'bg-red-100 text-red-800':
                        quotation.status.code === 'pending',
                      'bg-orange-100 text-orange-800':
                        quotation.status.code === 'dispatched',
                      'bg-green-100 text-green-800':
                        quotation.status.code === 'accepted',
                      'bg-gray-100 text-gray-800':
                        quotation.status.code === 'declined'
                    }"
                  >
                    {{ quotation.status.label }}
                  </span>
                </td>
                <td
                  class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-b"
                >
                  <div class="relative inline-block text-left">
                    <button
                      @click="
                        toggleDropdown(
                          quotation.userQuotationId ||
                            quotation.serviceRequestQuotationId
                        )
                      "
                      class="inline-flex justify-center w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Actions
                      <svg
                        class="w-4 h-4 ml-2 -mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>

                    <!-- Dropdown Menu -->
                    <div
                      v-if="
                        activeDropdown ===
                          (quotation.userQuotationId ||
                            quotation.serviceRequestQuotationId)
                      "
                      class="absolute right-0 z-10 w-48 mt-2 origin-top-right bg-white border border-gray-200 rounded-md shadow-lg ring-1 ring-black ring-opacity-5"
                    >
                      <div class="py-1">
                        <button
                          @click="handleAction(quotation, 'edit')"
                          class="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                        >
                          Edit
                        </button>
                        <button
                          @click="handleAction(quotation, 'whatsApp')"
                          class="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                        >
                          Send To Whatsapp
                        </button>
                        <button
                          @click="handleAction(quotation, 'print')"
                          class="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                        >
                          Print
                        </button>
                        <button
                          @click="handleAction(quotation, 'convertSale')"
                          class="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                        >
                          Convert To Sale
                        </button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="card-footer d-flex justify-content-center py-3">
          <el-pagination
            @current-change="handlePageChange"
            :current-page.sync="currentPage"
            :page-size="perPage"
            :total="totalItems"
            layout="prev, pager, next"
            background
          >
          </el-pagination>
        </div>
      </div>
    </div>

    <!-- Dialogs -->
    <el-dialog
      :visible.sync="showSaleQuotationModal"
      title="Sale Quotation Invoice"
      width="70%"
      top="5vh"
      custom-class="sale-quotation-invoice-dialog"
    >
      <component
        :is="quotationModalComponent"
        :data="quotationData"
        @close="showSaleQuotationModal = false"
      />
    </el-dialog>
    <el-dialog
      :visible.sync="isSaleConfirmationModal"
      title="Quotation to Sale "
      width="30%"
    >
      <div>
        <p class="py-4">
          Are you sure, do you want to covert quotation for customer
          <span
            class="capitalize"
            v-if="this.quotationData && this.quotationData.user"
          >
            {{
              this.quotationData.user.firstName +
                " " +
                this.quotationData.user.lastName
            }}
          </span>
          to sale ?
        </p>
        <div class="flex gap-4">
          <button
            @click="handleConvertQuotationToSale()"
            class="px-6 py-2 text-white bg-blue-500 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Yes
          </button>
          <button
            @click="isSaleConfirmationModal = false"
            class="px-6 py-2 text-white bg-blue-500 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            No
          </button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import headerComponent from "../Components/HeaderComponent";
import { mapActions, mapState } from "vuex";
import dateFns from "date-fns";
import { print } from "@/utils/print";
import SaleQuotationDesign from "./SaleQuotationDesign.vue";
import ServiceRequestQuotationDesign from "../ServiceRequestQuotation/ServiceRequestQuotationDesign.vue";

import {
  Select,
  Option,
  DatePicker,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Dialog,
  Button,
  Tag,
  Pagination
} from "element-ui";
import { mapGetters, mapMutations } from "vuex/dist/vuex.common.js";

function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function calendarMonthRange(offset = 0, refDate = new Date()) {
  const y = refDate.getFullYear();
  const m = refDate.getMonth() + offset;
  return {
    fromDate: formatLocalDate(new Date(y, m, 1)),
    toDate: formatLocalDate(new Date(y, m + 1, 0))
  };
}

function indianFinancialYearRange(period = "current", refDate = new Date()) {
  const y = refDate.getFullYear();
  const month = refDate.getMonth();
  let fyStartYear = month >= 3 ? y : y - 1;
  if (period === "prev") fyStartYear -= 1;
  return {
    fromDate: `${fyStartYear}-04-01`,
    toDate: `${fyStartYear + 1}-03-31`
  };
}

export default {
  components: {
    [Tag.name]: Tag,
    [Pagination.name]: Pagination,
    "header-component": headerComponent,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    "sale-quotation-design": SaleQuotationDesign,
    "service-request-quotation-design": ServiceRequestQuotationDesign
  },

  data() {
    const defaultFY = indianFinancialYearRange("current");
    return {
      showSaleQuotationModal: false,
      isSaleConfirmationModal: false,
      quotationData: {},
      quotationModalComponent: "sale-quotation-design",
      currentPage: 1,
      perPage: 10,
      activeDropdown: null,
      panelFilters: {
        fromDate: defaultFY.fromDate,
        toDate: defaultFY.toDate,
        quotationNumber: "",
        customerName: "",
        status: ""
      },
      appliedFilters: {
        fromDate: defaultFY.fromDate,
        toDate: defaultFY.toDate,
        quotationNumber: "",
        customerName: "",
        status: ""
      },
      _skipFilterWatchFetch: false
    };
  },
  computed: {
    ...mapState("user", ["userQuotations", "userQuotationMeta"]),
    ...mapGetters({
      loggedInUser: "user/getLoggedInUser",
      paymentModes: "paymentMode/getPaymentModes",
      statuses: "status/getStatusList"
    }),
    totalItems() {
      return this.userQuotationMeta.total || 0;
    },
    hasActiveFilters() {
      return Object.values(this.appliedFilters).some(v => v !== "");
    },
    activeDatePreset() {
      const f = this.panelFilters.fromDate;
      const t = this.panelFilters.toDate;
      if (!f || !t) return null;

      const cur = indianFinancialYearRange("current");
      if (f === cur.fromDate && t === cur.toDate) return "currentFY";

      const prev = indianFinancialYearRange("prev");
      if (f === prev.fromDate && t === prev.toDate) return "prevFY";

      const today = new Date();
      const todayStr = formatLocalDate(today);
      if (f === todayStr && t === todayStr) return "today";

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = formatLocalDate(yesterday);
      if (f === yesterdayStr && t === yesterdayStr) return "yesterday";

      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      if (f === formatLocalDate(startOfWeek) && t === todayStr) return "thisWeek";

      const thisMonth = calendarMonthRange(0, today);
      if (f === thisMonth.fromDate && t === thisMonth.toDate) return "thisMonth";

      const lastMonth = calendarMonthRange(-1, today);
      if (f === lastMonth.fromDate && t === lastMonth.toDate) return "lastMonth";

      return null;
    }
  },
  watch: {
    "panelFilters.fromDate"(newVal) {
      this.appliedFilters.fromDate = newVal;
      if (!this._skipFilterWatchFetch) this.fetchQuotations(1);
    },
    "panelFilters.toDate"(newVal) {
      this.appliedFilters.toDate = newVal;
      if (!this._skipFilterWatchFetch) this.fetchQuotations(1);
    },
    "panelFilters.status"(newVal) {
      this.appliedFilters.status = newVal;
      if (!this._skipFilterWatchFetch) this.fetchQuotations(1);
    }
  },
  beforeMount() {
    this.getPaymentModes();
    this.getStatuses();
    this.fetchQuotations();
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    ...mapActions("user", [
      "getUserQuotations",
      "getUserQuotation",
      "printQuotation"
    ]),
    ...mapActions("serviceRequestQuotation", [
      "getServiceRequestQuotationDetails",
      "updateServiceRequestQuotation"
    ]),
    handleClickOutside(event) {
      if (!event.target.closest(".relative")) {
        this.activeDropdown = null;
      }
    },
    ...mapActions("sale", ["createSale"]),
    ...mapActions({
      getPaymentModes: "paymentMode/getPaymentModes",
      getStatuses: "status/getStatusList",
      updateUserQuotation: "user/updateUserQuotation"
    }),
    ...mapMutations("sale", ["setSale"]),
    formatDate(value) {
      return dateFns.format(new Date(value), "YYYY-MM-DD");
    },
    isDetailedProductQuotation(quotation) {
      return quotation.quotationSource === "service_request_detailed_product";
    },
    fetchQuotations(page) {
      const p = page || this.currentPage;
      const params = { page: p, perPage: this.perPage };
      if (this.appliedFilters.fromDate) params.fromDate = this.appliedFilters.fromDate;
      if (this.appliedFilters.toDate) params.toDate = this.appliedFilters.toDate;
      if (this.appliedFilters.quotationNumber) params.quotationNumber = this.appliedFilters.quotationNumber;
      if (this.appliedFilters.customerName) params.customerName = this.appliedFilters.customerName;
      if (this.appliedFilters.status) params.status = this.appliedFilters.status;
      this.getUserQuotations(params);
    },
    applyFilters() {
      this.appliedFilters = { ...this.panelFilters };
      this.currentPage = 1;
      this.fetchQuotations(1);
    },
    applySingleFilter(filterName) {
      this.appliedFilters[filterName] = this.panelFilters[filterName];
      this.currentPage = 1;
      this.fetchQuotations(1);
    },
    clearAppliedFilter(filterName) {
      this.appliedFilters[filterName] = "";
      this.panelFilters[filterName] = "";
      this.currentPage = 1;
      this.fetchQuotations(1);
    },
    clearDateFilter() {
      this._skipFilterWatchFetch = true;
      this.panelFilters.fromDate = "";
      this.panelFilters.toDate = "";
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.appliedFilters.fromDate = "";
        this.appliedFilters.toDate = "";
        this.currentPage = 1;
        this.fetchQuotations(1);
      });
    },
    clearFilters() {
      this._skipFilterWatchFetch = true;
      const empty = { fromDate: "", toDate: "", quotationNumber: "", customerName: "", status: "" };
      this.panelFilters = { ...empty };
      this.appliedFilters = { ...empty };
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.currentPage = 1;
        this.fetchQuotations(1);
      });
    },
    setDateFilter(type) {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());

      let range;
      switch (type) {
        case "currentFY": range = indianFinancialYearRange("current"); break;
        case "prevFY": range = indianFinancialYearRange("prev"); break;
        case "today": range = { fromDate: formatLocalDate(today), toDate: formatLocalDate(today) }; break;
        case "yesterday": range = { fromDate: formatLocalDate(yesterday), toDate: formatLocalDate(yesterday) }; break;
        case "thisWeek": range = { fromDate: formatLocalDate(startOfWeek), toDate: formatLocalDate(today) }; break;
        case "thisMonth": range = calendarMonthRange(0, today); break;
        case "lastMonth": range = calendarMonthRange(-1, today); break;
        default: return;
      }
      this._skipFilterWatchFetch = true;
      this.panelFilters.fromDate = range.fromDate;
      this.panelFilters.toDate = range.toDate;
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.appliedFilters.fromDate = range.fromDate;
        this.appliedFilters.toDate = range.toDate;
        this.currentPage = 1;
        this.fetchQuotations(1);
      });
    },
    dateQuickFilterClass(preset) {
      const selected = this.activeDatePreset === preset;
      return [
        "px-2 py-1 text-xs rounded transition-colors",
        selected
          ? "text-blue-700 bg-blue-100 hover:bg-blue-200"
          : "text-gray-700 bg-gray-100 hover:bg-gray-200"
      ];
    },
    formatAppliedDateRange() {
      const f = this.appliedFilters.fromDate;
      const t = this.appliedFilters.toDate;
      if (f && t) {
        if (f === t) return new Date(f).toLocaleDateString("en-IN");
        return `${new Date(f).toLocaleDateString("en-IN")} - ${new Date(t).toLocaleDateString("en-IN")}`;
      }
      if (f) return `From ${new Date(f).toLocaleDateString("en-IN")}`;
      if (t) return `Until ${new Date(t).toLocaleDateString("en-IN")}`;
      return "";
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchQuotations(page);
    },
    async handleAction(userQuotation, command) {
      if (this.isDetailedProductQuotation(userQuotation)) {
        if (command === "edit") {
          this.$router.push("/quotation/" + userQuotation.serviceRequestQuotationId);
        }
        if (command === "print" || command === "whatsApp") {
          const quotationData = await this.getServiceRequestQuotationDetails(
            userQuotation.serviceRequestQuotationId
          );
          this.quotationData = quotationData;
          this.quotationModalComponent = "service-request-quotation-design";
          this.showSaleQuotationModal = true;
        }
        if (command === "convertSale") {
          this.quotationData = userQuotation;
          this.isSaleConfirmationModal = true;
        }
        return;
      }
      if (command === "edit") {
        this.$router.push("/sales/quotations/" + userQuotation.userQuotationId);
      }
      if (command === "print") {
        const quotationData = await this.getUserQuotation(
          userQuotation.userQuotationId
        );
        this.quotationData = quotationData;
        this.quotationModalComponent = "sale-quotation-design";
        print(
          { ...quotationData, quotationUser: quotationData.user },
          "sale-quotation"
        );
      }
      if (command === "whatsApp") {
        const quotationData = await this.getUserQuotation(
          userQuotation.userQuotationId
        );
        this.quotationData = quotationData;
        this.quotationModalComponent = "sale-quotation-design";
        this.showSaleQuotationModal = true;
      }
      if (command === "convertSale") {
        this.quotationData = userQuotation;
        this.isSaleConfirmationModal = true;
      }
    },
    toggleDropdown(quotationId) {
      this.activeDropdown =
        this.activeDropdown === quotationId ? null : quotationId;
    },
    async handleConvertQuotationToSale() {
      const isDetailedProduct = this.isDetailedProductQuotation(this.quotationData);
      const saleData = {
        userId: this.quotationData.userId,
        products: this.quotationData.productMeta.map(i => {
          return {
            productId: i.productId,
            name: i.name,
            price: i.price,
            quantity: i.quantity,
            discount: i.discount,
            isPercentage: i.isPercentage,
            productPriceTypeId: i.productPriceTypeId,
            taxRate: i.taxRate
          };
        }),
        date: this.quotationData.createdAt,
        createdBy:
          this.quotationData.createdBy ||
          (this.loggedInUser && this.loggedInUser.userId),
        discount: 0,
        paymentModeId: this.paymentModes[0].paymentModeId,
        paymentNote: "",
        amountPaid: 0
      };
      await this.setSale(saleData);
      this.createSale(saleData)
        .then(() => {
          const acceptedStatus = this.statuses.find(i => i.code === "accepted");
          const updatePromise = isDetailedProduct
            ? this.updateServiceRequestQuotation({
                serviceRequestQuotationId:
                  this.quotationData.serviceRequestQuotationId,
                statusId: acceptedStatus.statusId,
                serviceRequestId: this.quotationData.serviceRequestId,
                productMeta: this.quotationData.productMeta,
                total: this.quotationData.total,
                approvedBy:
                  this.loggedInUser && this.loggedInUser.userId
                    ? this.loggedInUser.userId
                    : undefined
              })
            : this.updateUserQuotation({
                ...this.quotationData,
                statusId: acceptedStatus.statusId
              });
          updatePromise.then(() => {
            this.fetchQuotations();
          });
          this.$notify({
            message: "Sale Saved Succesfully!",
            type: "success",
            title: "Success"
          });
          this.isSaleConfirmationModal = false;
        })
        .catch(err => {
          console.log("err", err);
        });
    }
  }
};
</script>
<style>
.dispatch-button {
  margin-left: 15px;
  margin-top: 20px;
}
.pending-status {
  background-color: #e72626;
  border-color: #e72626;
  color: #fff;
}
.dispatch-status {
  background-color: #f57316eb;
  border-color: #f57316eb;
  color: #fff;
}
.declined-status {
  background-color: #b0ccb3;
  border-color: #b0ccb3;
  color: #fff;
}
.sale-quotation-invoice-dialog {
  max-width: 960px;
}
.sale-quotation-invoice-dialog .el-dialog__body {
  overflow-x: hidden;
}
</style>
